import React, { useState, useEffect, useRef } from 'react';
import coachService from '../../services/endpoints/coachService';
import { CompetitionCalendarEntry, ShowPriority } from '../../types';
import { parseLocalDate } from '../../utils/dateUtils';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatIcsDate(dateStr: string): string {
  return dateStr.slice(0, 10).replace(/-/g, '');
}

function shiftIcsDate(dateStr: string, days: number): string {
  const d = parseLocalDate(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10).replace(/-/g, '');
}

function downloadIcs(entry: CompetitionCalendarEntry) {
  // Single 2-day all-day event: day before (travel/prep) through show day.
  // ICS DTEND is exclusive, so DTEND = show date + 1.
  const dayBeforeFmt = shiftIcsDate(entry.showDate, -1);
  const dayAfterFmt = shiftIcsDate(entry.showDate, 1);

  const detailLine = [entry.category, entry.division].filter(Boolean).join(' · ');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ShowTracker//EN',
    'BEGIN:VEVENT',
    `UID:show-${entry.showId}@showtracker`,
    `DTSTART;VALUE=DATE:${dayBeforeFmt}`,
    `DTEND;VALUE=DATE:${dayAfterFmt}`,
    `SUMMARY:${entry.showName} – ${entry.athleteName}`,
    ...(detailLine ? [`DESCRIPTION:${detailLine}. Athlete: ${entry.athleteName}`] : []),
    ...(entry.location ? [`LOCATION:${entry.location}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${entry.showName.replace(/\s+/g, '_')}_${entry.athleteName.replace(/\s+/g, '_')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

function googleCalendarUrl(entry: CompetitionCalendarEntry): string {
  // Same 2-day span: day before through show day (Google Calendar end is exclusive).
  const dayBeforeFmt = shiftIcsDate(entry.showDate, -1);
  const dayAfterFmt = shiftIcsDate(entry.showDate, 1);
  const detailLine = [entry.category, entry.division].filter(Boolean).join(' · ');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${entry.showName} – ${entry.athleteName}`,
    dates: `${dayBeforeFmt}/${dayAfterFmt}`,
    ...(detailLine ? { details: detailLine } : {}),
    ...(entry.location ? { location: entry.location } : {}),
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// ─── Priority chip ───────────────────────────────────────────────────────────

const PRIORITY_STYLES: Record<string, string> = {
  Primary:   'bg-gold-900/40 text-gold-300 border border-gold-700/40',
  Secondary: 'bg-blue-900/40 text-blue-300 border border-blue-700/40',
  Backup:    'bg-gray-700/60 text-gray-400 border border-gray-600/40',
  TuneUp:   'bg-purple-900/40 text-purple-300 border border-purple-700/40',
  Added:    'bg-green-900/40 text-green-300 border border-green-700/40',
};

const PriorityChip: React.FC<{ priority?: ShowPriority | string }> = ({ priority }) => {
  if (!priority) return null;
  const cls = PRIORITY_STYLES[priority] ?? PRIORITY_STYLES['Backup'];
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{priority}</span>;
};

// ─── Add to Calendar dropdown ────────────────────────────────────────────────

const AddToCalendarMenu: React.FC<{ entry: CompetitionCalendarEntry }> = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs px-3 py-1.5 bg-dark-600 hover:bg-dark-500 border border-dark-500 hover:border-gold-600/50 text-gray-300 hover:text-gold-300 rounded transition-colors whitespace-nowrap"
      >
        + Calendar
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-dark-700 border border-dark-600 rounded-lg shadow-xl z-20 py-1">
          <button
            onClick={() => { downloadIcs(entry); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white transition-colors"
          >
            📥 Download .ics
          </button>
          <a
            href={googleCalendarUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white transition-colors"
          >
            📅 Google Calendar ↗
          </a>
        </div>
      )}
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysFromTodayStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

// ─── Main component ──────────────────────────────────────────────────────────

type QuickFilter = 30 | 60 | 90;
type FilterMode = QuickFilter | 'custom';

const CompetitionCalendar: React.FC = () => {
  const [entries, setEntries] = useState<CompetitionCalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState<FilterMode>(90);
  const [customFrom, setCustomFrom] = useState<string>(todayStr);
  const [customTo, setCustomTo] = useState<string>(() => daysFromTodayStr(365));

  useEffect(() => {
    coachService.getCompetitionCalendar()
      .then(setEntries)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const now = new Date();

  let filtered: CompetitionCalendarEntry[];
  if (filterMode === 'custom') {
    const from = customFrom ? parseLocalDate(customFrom) : now;
    const to   = customTo   ? parseLocalDate(customTo)   : null;
    filtered = entries.filter(e => {
      const d = parseLocalDate(e.showDate);
      return d >= from && (!to || d <= to);
    });
  } else {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() + filterMode);
    filtered = entries.filter(e => parseLocalDate(e.showDate) <= cutoff);
  }

  if (filtered.length === 0 && entries.length === 0) return null;

  // Group by "Month YYYY"
  const groups: { label: string; items: CompetitionCalendarEntry[] }[] = [];
  for (const entry of filtered) {
    const label = parseLocalDate(entry.showDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(entry);
    } else {
      groups.push({ label, items: [entry] });
    }
  }

  const emptyMessage = filterMode === 'custom'
    ? 'No shows scheduled in the selected date range.'
    : `No shows scheduled in the next ${filterMode} days.`;

  return (
    <div className="bg-dark-800 rounded-xl border border-dark-700 p-5">
      {/* Header + filter */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-white">🗓️ Competition Calendar</h2>
        <div className="flex gap-1">
          {([30, 60, 90] as QuickFilter[]).map(d => (
            <button
              key={d}
              onClick={() => setFilterMode(d)}
              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                filterMode === d
                  ? 'bg-gold-600 text-black font-semibold'
                  : 'bg-dark-700 text-gray-400 hover:text-white hover:bg-dark-600'
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => setFilterMode('custom')}
            className={`text-xs px-3 py-1.5 rounded transition-colors ${
              filterMode === 'custom'
                ? 'bg-gold-600 text-black font-semibold'
                : 'bg-dark-700 text-gray-400 hover:text-white hover:bg-dark-600'
            }`}
          >
            Custom
          </button>
        </div>
      </div>

      {/* Custom date inputs */}
      {filterMode === 'custom' && (
        <div className="flex items-center gap-2 mb-4">
          <label className="text-xs text-gray-500 shrink-0">From</label>
          <input
            type="date"
            value={customFrom}
            onChange={e => setCustomFrom(e.target.value)}
            className="text-xs bg-dark-700 border border-dark-600 rounded px-2 py-1.5 text-gray-300 focus:border-gold-600 focus:outline-none"
          />
          <label className="text-xs text-gray-500 shrink-0">To</label>
          <input
            type="date"
            value={customTo}
            min={customFrom}
            onChange={e => setCustomTo(e.target.value)}
            className="text-xs bg-dark-700 border border-dark-600 rounded px-2 py-1.5 text-gray-300 focus:border-gold-600 focus:outline-none"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-gray-500 text-sm py-2">{emptyMessage}</p>
      ) : (
        <div className="space-y-5">
          {groups.map(({ label, items }) => (
            <div key={label}>
              <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">{label}</div>
              <div className="space-y-2">
                {items.map(entry => {
                  const d = parseLocalDate(entry.showDate);
                  const dayNum = d.toLocaleDateString('en-US', { day: 'numeric' });
                  const dayShort = d.toLocaleDateString('en-US', { weekday: 'short' });
                  const monthShort = d.toLocaleDateString('en-US', { month: 'short' });
                  const daysOut = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  const weeksOut = Math.floor(daysOut / 7);

                  return (
                    <div
                      key={entry.showId}
                      className="flex items-center gap-3 bg-dark-700 rounded-lg px-4 py-3"
                    >
                      {/* Date badge */}
                      <div className="shrink-0 w-14 text-center bg-dark-900/60 rounded-lg py-1.5">
                        <div className="text-xs text-gray-500 uppercase">{monthShort}</div>
                        <div className="text-xl font-bold text-gold-400 leading-none">{dayNum}</div>
                        <div className="text-xs text-gray-500">{dayShort}</div>
                      </div>

                      {/* Show info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium">{entry.athleteName}</span>
                          <span className="text-gray-500">·</span>
                          <span className="text-gray-300">{entry.showName}</span>
                          <PriorityChip priority={entry.showPriority} />
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {entry.location && (
                            <span className="text-xs text-gray-500">📍 {entry.location}</span>
                          )}
                          {(entry.category || entry.division) && (
                            <span className="text-xs text-gray-500">
                              {[entry.category, entry.division].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Countdown + calendar button */}
                      <div className="shrink-0 flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs text-gray-400">
                            {weeksOut > 0 ? `${weeksOut}w ${daysOut % 7}d` : `${daysOut}d`}
                          </div>
                          <div className="text-xs text-gray-600">out</div>
                        </div>
                        <AddToCalendarMenu entry={entry} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompetitionCalendar;
